// Étape « Usager » du guichet : recherche d'un compte citoyen par identifiant (téléphone ou
// courriel), création du compte si besoin (inscription puis code reçu par l'usager), vérification
// de l'identité sur pièce (type et date, sans numéro conservé), ou tiers sans compte.
import { TYPES_PIECE_IDENTITE } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DateChamp, GrilleChamps, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { useSession } from '../../../session/FournisseurSession';
import { peut } from '../../../session/permissions';
import { LIBELLES_PIECE_IDENTITE, type SaisieGuichet, type UsagerRecherche } from './etapes';
import commun from '../../../commun/commun.module.css';
import styles from '../metier.module.css';

function CreationCompte({ identifiant, onCree }: { identifiant: string; onCree: (identifiant: string) => void }) {
  const parCourriel = identifiant.includes('@');
  const [s, setS] = useState({ nom: '', prenoms: '', mot_de_passe: '', code: '' });
  const [envoye, setEnvoye] = useState(false);
  const inscription = useAction({
    executer: () =>
      requete('/public/inscription', {
        methode: 'POST',
        corps: { [parCourriel ? 'courriel' : 'telephone']: identifiant, canal: parCourriel ? 'courriel' : 'sms', mot_de_passe: s.mot_de_passe, nom: s.nom, prenoms: s.prenoms || undefined },
      }),
    succes: `Code à six chiffres envoyé à l'usager par ${parCourriel ? 'courriel' : 'SMS'} (valable 15 minutes).`,
    apres: () => setEnvoye(true),
  });
  const confirmation = useAction({
    executer: () => requete<{ compte_id: string; identifiant: string }>('/public/inscription/confirmer', { methode: 'POST', corps: { identifiant, code: s.code } }),
    succes: 'Compte usager créé.',
    apres: (r) => onCree(r.identifiant),
  });
  const maj = (cle: keyof typeof s) => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: e.target.value }));
  return (
    <fieldset className={styles.pile} style={{ border: '1px solid var(--cc-ligne)', borderRadius: 'var(--cc-rayon-2)', padding: 'var(--cc-esp-2)', margin: 0 }}>
      <legend>Créer le compte de l&apos;usager ({identifiant})</legend>
      {!envoye ? (
        <>
          <GrilleChamps>
            <Champ libelle="Nom" value={s.nom} required onChange={maj('nom')} erreur={inscription.erreursChamps.find((c) => c.champ === 'nom')?.message} />
            <Champ libelle="Prénoms" value={s.prenoms} facultatif onChange={maj('prenoms')} />
          </GrilleChamps>
          <Champ libelle="Mot de passe initial choisi par l'usager" type="password" autoComplete="new-password" value={s.mot_de_passe} required aide="L'usager le saisit lui-même ou le change ensuite depuis le portail ; il n'est jamais affiché." onChange={maj('mot_de_passe')} erreur={inscription.erreursChamps.find((c) => c.champ === 'mot_de_passe')?.message} />
          {inscription.messageErreur ? <Bandeau type="erreur">{inscription.messageErreur}</Bandeau> : null}
          <div>
            <Bouton variante="secondaire" chargement={inscription.enCours} disabled={!s.nom.trim() || s.mot_de_passe.length < 8} onClick={() => inscription.lancer(undefined)}>
              Envoyer le code à l&apos;usager
            </Bouton>
          </div>
        </>
      ) : (
        <>
          <Champ libelle="Code à six chiffres reçu par l'usager" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} className="cc-mono" value={s.code} required onChange={maj('code')} />
          {confirmation.messageErreur ? <Bandeau type="erreur">{confirmation.messageErreur}</Bandeau> : null}
          <div className={commun.ligne}>
            <Bouton variante="principal" chargement={confirmation.enCours} disabled={!/^\d{6}$/.test(s.code)} onClick={() => confirmation.lancer(undefined)}>
              Confirmer et créer le compte
            </Bouton>
            <Bouton variante="discret" onClick={() => setEnvoye(false)}>
              Renvoyer un code
            </Bouton>
          </div>
        </>
      )}
    </fieldset>
  );
}

function VerificationIdentite({ usager, onVerifie }: { usager: UsagerRecherche; onVerifie: (u: UsagerRecherche) => void }) {
  const [s, setS] = useState({ type_piece: 'carte_nationale_identite', date_piece: '', observations: '' });
  const verification = useAction({
    executer: () => requete<UsagerRecherche & Record<string, unknown>>(`/usagers/${usager.id}/verifier-identite`, { methode: 'POST', corps: { type_piece: s.type_piece, date_piece: s.date_piece, observations: s.observations || undefined } }),
    succes: 'Identité vérifiée sur pièce : consignée sans conserver le numéro de la pièce.',
    apres: (p) => onVerifie({ ...usager, identite_verifiee: p.identite_verifiee }),
  });
  return (
    <div className={styles.pile}>
      <GrilleChamps>
        <Selection libelle="Pièce présentée" value={s.type_piece} options={TYPES_PIECE_IDENTITE.map((t) => ({ valeur: t, libelle: LIBELLES_PIECE_IDENTITE[t] ?? t }))} onChange={(e) => setS((x) => ({ ...x, type_piece: e.target.value }))} />
        <DateChamp libelle="Date de la pièce (délivrance)" value={s.date_piece} required max={new Date().toISOString().slice(0, 10)} onChange={(e) => setS((x) => ({ ...x, date_piece: e.target.value }))} erreur={verification.erreursChamps.find((c) => c.champ === 'date_piece')?.message} />
      </GrilleChamps>
      <ZoneTexte libelle="Observations" value={s.observations} rows={2} facultatif onChange={(e) => setS((x) => ({ ...x, observations: e.target.value }))} />
      {verification.messageErreur ? <Bandeau type="erreur">{verification.messageErreur}</Bandeau> : null}
      <div>
        <Bouton variante="secondaire" chargement={verification.enCours} disabled={!s.date_piece} onClick={() => verification.lancer(undefined)}>
          Consigner la vérification d&apos;identité
        </Bouton>
      </div>
    </div>
  );
}

export function EtapeUsager({ saisie, onChanger, identiteRequise }: { saisie: SaisieGuichet; onChanger: (s: Partial<SaisieGuichet>) => void; identiteRequise: boolean }) {
  const { contexte } = useSession();
  const [identifiant, setIdentifiant] = useState('');
  const [resultat, setResultat] = useState<'introuvable' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [creation, setCreation] = useState(false);

  const rechercher = async (ident = identifiant) => {
    setEnCours(true);
    setErreur(null);
    setResultat(null);
    try {
      const u = await requete<UsagerRecherche>('/usagers/recherche', { parametres: { identifiant: ident.trim() } });
      onChanger({ usager: u, tiers_nom: '' });
      setCreation(false);
    } catch (e) {
      if (e instanceof ErreurApi && e.statut === 404) setResultat('introuvable');
      else setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className={styles.pile}>
      <fieldset className={styles.pile} style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="cc-etiquette">Qui dépose la demande</legend>
        <div className={styles.choixResultat}>
          {[
            { v: 'compte' as const, l: 'Usager inscrit sur le portail (compte citoyen)' },
            { v: 'tiers' as const, l: 'Personne ou organisme sans compte (le dossier reste suivi au guichet)' },
          ].map((o) => (
            <label key={o.v} className={styles.optionResultat} data-choisi={saisie.mode_usager === o.v || undefined}>
              <input type="radio" name="mode-usager" checked={saisie.mode_usager === o.v} onChange={() => onChanger({ mode_usager: o.v })} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {saisie.mode_usager === 'tiers' ? (
        <GrilleChamps>
          <Champ id="guichet-tiers" libelle="Nom du demandeur" value={saisie.tiers_nom} required onChange={(e) => onChanger({ tiers_nom: e.target.value, usager: null })} />
          <Champ id="guichet-telephone" libelle="Téléphone" type="tel" value={saisie.telephone} facultatif onChange={(e) => onChanger({ telephone: e.target.value })} />
          <Champ id="guichet-courriel" libelle="Courriel" type="email" value={saisie.courriel} facultatif onChange={(e) => onChanger({ courriel: e.target.value })} />
        </GrilleChamps>
      ) : (
        <>
          <form
            className={commun.ligne}
            style={{ alignItems: 'flex-end' }}
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (identifiant.trim()) void rechercher();
            }}
          >
            <Champ id="guichet-identifiant" libelle="Téléphone ou courriel de l'usager" value={identifiant} aide="Recherche exacte sur l'identifiant de connexion du compte." onChange={(e) => setIdentifiant(e.target.value)} />
            <Bouton type="submit" variante="secondaire" chargement={enCours} disabled={!identifiant.trim()}>
              Rechercher
            </Bouton>
          </form>
          {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
          {resultat === 'introuvable' && !creation ? (
            <Bandeau type="info">
              Aucun compte usager pour cet identifiant.{' '}
              <Bouton petit variante="secondaire" onClick={() => setCreation(true)}>
                Créer le compte
              </Bouton>
            </Bandeau>
          ) : null}
          {creation ? <CreationCompte identifiant={identifiant.trim()} onCree={(ident) => void rechercher(ident)} /> : null}
          {saisie.usager ? (
            <div className={styles.pile} data-testid="usager-trouve">
              <dl className={commun.definitions}>
                <dt>Usager</dt>
                <dd>
                  {[saisie.usager.prenoms, saisie.usager.nom].filter(Boolean).join(' ')} <span className="cc-mono">({saisie.usager.identifiant})</span>
                </dd>
                <dt>Identité</dt>
                <dd>
                  {saisie.usager.identite_verifiee
                    ? `Vérifiée le ${date(saisie.usager.identite_verifiee.le)} sur ${LIBELLES_PIECE_IDENTITE[saisie.usager.identite_verifiee.type_piece] ?? saisie.usager.identite_verifiee.type_piece}`
                    : identiteRequise
                      ? 'Déclarée seulement : cette démarche exige une identité vérifiée sur pièce avant le dépôt.'
                      : 'Déclarée seulement (vérification facultative pour cette démarche).'}
                </dd>
              </dl>
              {!saisie.usager.identite_verifiee && peut(contexte, 'dossier', 'modifier') ? <VerificationIdentite usager={saisie.usager} onVerifie={(u) => onChanger({ usager: u })} /> : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
